// AdminPanel → Routing → "DLT TMD" sub-tab.
//
// Read-only answer to: "what will TLV 0x1402 actually carry for this tenant?"
//
// The chain is PEID , TM-A [, TM-D]:
//   PEID  — the tenant's DLT Principal Entity id
//   TM-A  — ComBird's own telemarketer id (platform constant; empty = chain OFF)
//   TM-D  — the downstream telemarketer, resolved per sender header:
//             direct   → none (chain ends at ComBird)
//             explicit → that header's own TMD
//             inherit  → the TM id of the operator the traffic routes through
//                        for that SMS type (force-global → tenant → global)
//
// Every value here is resolved server-side by the same rule the sender uses
// (mirrors sms_check::dlt_chain, parity-tested) — this tab never recomputes
// the chain in JS, because a second copy of that rule is exactly how a chain
// silently lost its TM-D tail and messages started getting rejected.
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {
  Box, Card, CardContent, Chip, CircularProgress, IconButton, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography, Alert,
} from '@mui/material';
import {Refresh, WarningAmber} from '@mui/icons-material';
import Api from '../../api';
import {DLT_CHAIN_PREVIEW_URL} from '../../lib/constant';

const TYPE_LABEL = {
  otp: 'OTP',
  transactional: 'Transactional',
  promotional: 'Promotional',
};

// Where the route came from — mirrors the resolver's tiers. force_global is
// called out because it silently outranks everything the Routing tab shows.
const ROUTE_SOURCE = {
  force_global: {label: 'force-global override', color: 'warning'},
  tenant: {label: 'tenant route', color: 'primary'},
  global: {label: 'global default', color: 'default'},
  none: {label: 'no route', color: 'error'},
};

const MODE_COLOR = {
  direct: 'warning',
  explicit: 'primary',
  inherit: 'success',
};

// The API returns camelCase or snake_case depending on the transcoding path.
const pick = (o, camel, snake) => (o ? (o[camel] !== undefined ? o[camel] : o[snake]) : undefined);

export default class DltTmdTab extends Component {
  static propTypes = {
    tenantId: PropTypes.string,
  };

  state = {loading: false, error: '', data: null};

  componentDidMount() {
    this.fetchPreview();
  }

  componentDidUpdate(prev) {
    if (prev.tenantId !== this.props.tenantId) this.fetchPreview();
  }

  fetchPreview = async () => {
    const {tenantId} = this.props;
    if (!tenantId) return;
    this.setState({loading: true, error: ''});
    try {
      const url = DLT_CHAIN_PREVIEW_URL.replace('{instId}', tenantId);
      const res = await Api.get(url);
      this.setState({data: res?.data?.data || res?.data || null, loading: false});
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load DLT chain preview';
      this.setState({error: msg, loading: false});
    }
  };

  renderRow = (row, idx) => {
    const senderId = pick(row, 'senderId', 'sender_id') || '-';
    const smsType = pick(row, 'smsType', 'sms_type') || '';
    const operator = pick(row, 'operatorName', 'operator_name') || '';
    const routeSource = pick(row, 'routeSource', 'route_source') || 'none';
    const tmdMode = pick(row, 'tmdMode', 'tmd_mode') || '';
    const resolvedTmd = pick(row, 'resolvedTmd', 'resolved_tmd') || '';
    const tmdSource = pick(row, 'tmdSource', 'tmd_source') || 'none';
    const chain = row.chain || '';
    const warning = row.warning || '';
    const src = ROUTE_SOURCE[routeSource] || ROUTE_SOURCE.none;

    return (
      <TableRow key={`${senderId}-${smsType}-${idx}`} sx={warning ? {bgcolor: '#fff8e1'} : undefined}>
        <TableCell><code>{senderId}</code></TableCell>
        <TableCell>{TYPE_LABEL[smsType] || smsType}</TableCell>
        <TableCell>
          {operator ? <code>{operator}</code> : <span style={{color: '#999'}}>—</span>}
          <Chip size="small" variant="outlined" color={src.color} label={src.label} sx={{ml: 1}} />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            color={MODE_COLOR[tmdMode] || 'default'}
            variant={tmdMode === 'direct' ? 'filled' : 'outlined'}
            label={tmdMode || 'legacy'}
          />
        </TableCell>
        <TableCell>
          {resolvedTmd ? (
            <>
              <code>{resolvedTmd}</code>
              <Typography variant="caption" color="text.secondary" component="div">
                from {tmdSource}
              </Typography>
            </>
          ) : (
            <span style={{color: '#c77700'}}>none — chain ends at ComBird</span>
          )}
        </TableCell>
        <TableCell sx={{maxWidth: 320}}>
          <code style={{fontSize: 12, wordBreak: 'break-all'}}>{chain || '— no 0x1402 —'}</code>
          {warning ? (
            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5}}>
              <WarningAmber sx={{fontSize: 16, color: '#c77700', mt: '2px'}} />
              <Typography variant="caption" sx={{color: '#c77700'}}>{warning}</Typography>
            </Box>
          ) : null}
        </TableCell>
      </TableRow>
    );
  };

  render() {
    const {tenantId} = this.props;
    const {loading, error, data} = this.state;

    const entityId = pick(data, 'entityId', 'entity_id') || '';
    const combirdTma = pick(data, 'combirdTma', 'combird_tma') || '';
    const chainEnabled = pick(data, 'chainEnabled', 'chain_enabled');
    const rows = data?.rows || [];
    const overrides = pick(data, 'templateOverrides', 'template_overrides') || [];
    const warned = rows.filter(r => r.warning).length;

    return (
      <Box>
        <Card sx={{mb: 2}}>
          <CardContent>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
              <Box sx={{minWidth: 0}}>
                <Typography variant="h6">DLT TM Chain (TLV 0x1402)</Typography>
                <Typography variant="caption" color="text.secondary">
                  What each sender header will actually send, per SMS type. Resolved server-side by the same rule
                  the sender uses — PEID, ComBird TM-A, then the downstream TM-D from the header&apos;s mode.
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 0.5}}>
                  PEID: <code>{entityId || '(not set)'}</code> &nbsp;·&nbsp;
                  ComBird TM-A: <code>{combirdTma || '(not set)'}</code>
                </Typography>
              </Box>
              <Tooltip title="Refresh">
                <span>
                  <IconButton onClick={this.fetchPreview} disabled={loading}>
                    <Refresh />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>

        {chainEnabled === false ? (
          <Alert severity="error" sx={{mb: 2}}>
            ComBird TM-A is not configured, so <strong>no 0x1402 TLV is sent at all</strong> — the DLT chain is
            switched off platform-wide.
          </Alert>
        ) : null}
        {warned > 0 ? (
          <Alert severity="warning" sx={{mb: 2}}>
            {warned} row{warned === 1 ? '' : 's'} may be rejected by DLT scrubbing — the route operator expects a
            TM-D the chain does not carry. Set that header&apos;s TMD mode to <strong>inherit</strong> in SMS Config.
          </Alert>
        ) : null}

        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}><CircularProgress /></Box>
            ) : !tenantId ? (
              <Typography variant="body2" color="text.secondary" sx={{py: 3, textAlign: 'center'}}>
                No tenant selected.
              </Typography>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : !rows.length ? (
              <Typography variant="body2" color="text.secondary" sx={{py: 3, textAlign: 'center'}}>
                No sender headers configured for this tenant.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Header</TableCell>
                    <TableCell>SMS Type</TableCell>
                    <TableCell>Routes via</TableCell>
                    <TableCell>TMD mode</TableCell>
                    <TableCell>Resolved TM-D</TableCell>
                    <TableCell>Chain sent (0x1402)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{rows.map(this.renderRow)}</TableBody>
              </Table>
            )}

            {overrides.length ? (
              <Box sx={{mt: 3}}>
                <Typography variant="subtitle2">Per-template TMD overrides</Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{mb: 1}}>
                  These templates override the header&apos;s TM-D. An override wins over the header mode for that
                  template only.
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Template</TableCell>
                      <TableCell>Header</TableCell>
                      <TableCell>TM-D override</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overrides.map((o, i) => (
                      <TableRow key={(pick(o, 'templateId', 'template_id') || '') + i}>
                        <TableCell>{pick(o, 'templateName', 'template_name') || pick(o, 'templateId', 'template_id')}</TableCell>
                        <TableCell><code>{pick(o, 'senderId', 'sender_id')}</code></TableCell>
                        <TableCell><code>{pick(o, 'dltTmdIdOverride', 'dlt_tmd_id_override')}</code></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : null}
          </CardContent>
        </Card>
      </Box>
    );
  }
}
